import { useState, useEffect, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { getProfile, recordPolicyAcceptance, ensureProfile } from '../../services/firestore/profileService';
import { saveConsentRecord } from '../../services/firestore/dataopsService';
import { getSessionId } from '../../utils/sessionId';
import { logAudit } from '../../utils/auditLog';
import type { ConsentRecord } from '../../types/dataops';

// Current policy versions — bump these when policies change
export const CURRENT_TOS_VERSION = '1.0.0';
export const CURRENT_PRIVACY_VERSION = '1.0.0';

interface Props {
  children: ReactNode;
}

export function ReconsentGate({ children }: Props) {
  const [status, setStatus] = useState<'loading' | 'needs_consent' | 'accepted'>('loading');
  const [accepting, setAccepting] = useState(false);
  const sessionId = getSessionId();

  useEffect(() => {
    (async () => {
      try {
        const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000));
        const profile = await Promise.race([getProfile(sessionId), timeout]);
        if (!profile) {
          // No profile yet (or timeout) — user hasn't started; let them through
          // The triage flow will create profile + collect initial consent
          setStatus('accepted');
          return;
        }

        const tosOk = profile.acceptedTosVersion === CURRENT_TOS_VERSION;
        const privacyOk = profile.acceptedPrivacyVersion === CURRENT_PRIVACY_VERSION;

        if (tosOk && privacyOk) {
          setStatus('accepted');
        } else {
          setStatus('needs_consent');
        }
      } catch {
        // On error, let user through rather than blocking
        setStatus('accepted');
      }
    })();
  }, [sessionId]);

  async function handleAccept() {
    setAccepting(true);
    try {
      // Ensure profile exists
      await ensureProfile(sessionId);

      // Record policy acceptance
      await recordPolicyAcceptance(sessionId, CURRENT_TOS_VERSION, CURRENT_PRIVACY_VERSION);

      // Record consent events for audit trail
      const now = new Date().toISOString();
      const baseRecord: Omit<ConsentRecord, 'id' | 'purpose' | 'consentText'> = {
        userId: sessionId,
        action: 'granted',
        policyVersion: CURRENT_TOS_VERSION,
        method: 'modal_accept',
        ipAddress: null,
        userAgent: navigator.userAgent,
        timestamp: now,
        expiresAt: null,
        withdrawalReason: null,
        effectiveImmediately: true,
      };

      await saveConsentRecord({
        ...baseRecord,
        id: `consent-${sessionId.slice(0, 8)}-tos-accept-${Date.now()}`,
        purpose: 'lab_data_collection',
        consentText: `Accepted Terms of Service v${CURRENT_TOS_VERSION} and Privacy Policy v${CURRENT_PRIVACY_VERSION}`,
      });

      logAudit('consent.granted', 'policy_acceptance', {
        tos_version: CURRENT_TOS_VERSION,
        privacy_version: CURRENT_PRIVACY_VERSION,
      });

      setStatus('accepted');
    } catch (err) {
      console.error('Failed to record policy acceptance:', err);
      // Let user through on error rather than permanently blocking
      setStatus('accepted');
    } finally {
      setAccepting(false);
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-bg-deep text-text-primary flex items-center justify-center">
        <p className="text-text-muted text-lg">Loading...</p>
      </div>
    );
  }

  if (status === 'accepted') {
    return <>{children}</>;
  }

  // Re-consent interstitial
  return (
    <div className="min-h-screen bg-bg-deep text-text-primary font-sans flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full bg-bg-card rounded-2xl border border-bg-tertiary p-8"
      >
        <h1 className="font-display text-2xl font-bold text-gold mb-3">
          Updated Policies
        </h1>
        <p className="text-text-secondary text-base leading-relaxed mb-6">
          We've updated our Terms of Service and Privacy Policy. Please review the changes and accept to continue using HemoLogix.
        </p>

        <div className="space-y-3 mb-6">
          <div className="p-4 rounded-xl bg-bg-deep border border-bg-tertiary">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium">Terms of Service</span>
              <span className="text-xs text-accent bg-accent/10 px-2 py-0.5 rounded-full">
                v{CURRENT_TOS_VERSION}
              </span>
            </div>
            <p className="text-text-muted text-sm">
              Covers service usage, health disclaimers, dispute resolution, and account management.
            </p>
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent text-sm hover:underline mt-1 inline-block"
            >
              Read full terms &rarr;
            </a>
          </div>

          <div className="p-4 rounded-xl bg-bg-deep border border-bg-tertiary">
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium">Privacy Policy</span>
              <span className="text-xs text-accent bg-accent/10 px-2 py-0.5 rounded-full">
                v{CURRENT_PRIVACY_VERSION}
              </span>
            </div>
            <p className="text-text-muted text-sm">
              Covers data collection, consent, sharing, retention, your rights, and AI processing.
            </p>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent text-sm hover:underline mt-1 inline-block"
            >
              Read full policy &rarr;
            </a>
          </div>
        </div>

        <button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full py-4 rounded-xl bg-accent text-white font-semibold text-lg transition-colors hover:bg-accent-light disabled:opacity-50"
        >
          {accepting ? 'Accepting...' : 'I Accept'}
        </button>

        <p className="text-xs text-text-muted text-center mt-4 leading-relaxed">
          By clicking "I Accept", you agree to our updated{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>.
        </p>
      </motion.div>
    </div>
  );
}
